import { ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type Tone = 'neutral' | 'blue' | 'green' | 'amber' | 'purple';

const toneText: Record<Tone, string> = {
  neutral: 'text-muted-foreground',
  blue: 'text-blue-600 dark:text-blue-400',
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  purple: 'text-purple-600 dark:text-purple-400',
};

interface CollapsibleBlockProps {
  icon?: ReactNode;
  label: string;
  summary?: string;
  defaultOpen?: boolean;
  tone?: Tone;
  children: ReactNode;
}

export default function CollapsibleBlock({
  icon,
  label,
  summary,
  defaultOpen = false,
  tone = 'neutral',
  children,
}: CollapsibleBlockProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-md border border-border/60 bg-muted/20">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/40"
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90'
          )}
        />
        {icon && <span className={cn('shrink-0', toneText[tone])}>{icon}</span>}
        <span className={cn('font-medium', toneText[tone])}>{label}</span>
        {summary && (
          <span className="truncate text-muted-foreground/80">{summary}</span>
        )}
      </button>
      {open && <div className="border-t border-border/60 p-2">{children}</div>}
    </div>
  );
}
